import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import {
  ReceivingReportRecord,
  ReceivingReportStatusForm,
  RRNextStatus,
} from '../../../../types/ReceivingReport';
import { Button, Card, Form, Input, Space, Timeline } from 'antd';
import { RequestStatusEnum } from '../../../../lib/enums';
import { useAuthStore } from '../../../../store';
import React, { useEffect, useState } from 'react';
import { useUpdateStatus } from '../../../../hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { MaterialRequestStatusForm } from '../../../../types/MaterialRequest';
import { isModuleApproved } from '../../../../lib/utils';
import { RRS_API } from '../../../../hooks/useReceivingReportHooks';
import { TimelineItemProps } from 'antd/es/timeline/TimelineItem';
import TimelineView from '../../../../components/TimelineView';
import { StatusHistory } from '../../../../types';

interface ReceivingReportInfoStatusProps {
  data?: ReceivingReportRecord;
  statusHistory: StatusHistory[];
}

const ReceivingReportInfoStatus: React.FC<ReceivingReportInfoStatusProps> = ({
  data,
  statusHistory,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const params = useParams();
  const { mutate, isPending, isSuccess, isError, error } = useUpdateStatus(
    `${RRS_API}/${params.id}/status`,
  );

  const [cancelClicked, setCancelClicked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const userModuleAccessRole = user?.moduleAccess?.find(
    (item) => item.module.code === 'RR',
  );

  const onFinish = (values: any) => {
    const checkAndSetError = (
      field: keyof ReceivingReportRecord,
      errorMessage: string,
    ) => {
      if (data?.[field] === undefined || data?.[field] === null) {
        setIsErrorModalOpen(true);
        setErrorMess(errorMessage);
        return true; // Indicate that an error occurred
      }
      return false; // Indicate no error
    };

    // check if the required fields have values
    if (
      checkAndSetError('requiredDate', 'Please fill up required date') ||
      checkAndSetError('destination', 'Please fill up destination')
    ) {
      return; // Stop further processing if any error occurs
    }

    // Proceed with further processing if all required fields have values
    const form: MaterialRequestStatusForm = {
      status: RRNextStatus(data?.status),
      remarks: values.remarks,
    };
    mutate({
      id: data?.id,
      form: form,
    });
  };

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error?.response?.data.message);
    }
  }, [isSuccess, isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/receiving-report');
  };

  const handleCancelClick = () => {
    const textValue = form.getFieldValue('remarks');
    if (!textValue?.trim()) {
      setCancelClicked(true);
      setErrorMessage('Text is required');
      setTimeout(() => {
        setErrorMessage('');
      }, 2000);
    } else {
      setCancelClicked(false);
      const form: ReceivingReportStatusForm = {
        status: RequestStatusEnum.CANCEL,
        remarks: textValue,
      };
      mutate({
        id: params.id,
        form: form,
      });
    }
  };

  const renderCancelButton = () => {
    const isAdmin = user?.role === 'ROLE_ADMIN';
    const canCancel =
      userModuleAccessRole?.cancel &&
      (data?.status === RequestStatusEnum.DRAFT ||
        data?.status === RequestStatusEnum.CONFIRM);

    if (isAdmin || canCancel) {
      return (
        <Button danger onClick={handleCancelClick} loading={isPending}>
          Cancel
        </Button>
      );
    }
    return null;
  };
  let apiTimelineItems: TimelineItemProps[] = [];

  if (statusHistory && statusHistory.length > 0) {
    apiTimelineItems = statusHistory
      .map((item) => ({
        key: item.id,
        children: <TimelineView item={item} />,
      }))
      .sort((a, b) => {
        return parseInt(b.key) - parseInt(a.key);
      });
  }
  const timelineItems = [
    {
      key: 'status',
      children: (
        <div className={'space-y-2'}>
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              name="remarks"
              rules={[
                { required: cancelClicked, message: 'Please input your text!' },
              ]}
              validateStatus={cancelClicked ? 'error' : undefined}
              className={cancelClicked ? 'ant-form-item-has-error' : ''}
              help={cancelClicked ? errorMessage : ''}
            >
              <Input.TextArea rows={4} placeholder="Input your text here" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isPending}>
                  {RRNextStatus(data?.status)}
                </Button>
                {renderCancelButton()}
              </Space>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    ...apiTimelineItems,
    {
      key: 'Created',
      children: (
        <div className={'space-y-2 border rounded p-2'}>
          <div>
            <span className={'font-semibold'}>{data?.code}</span> requested by{' '}
            <span className={'font-semibold'}>{data?.createdBy}</span> requested
            date <span className={'font-semibold'}>{data?.timeCreated}</span>.
          </div>
        </div>
      ),
    },
  ];

  const filteredTimelineItems = timelineItems.filter((item) => {
    if (item.key !== 'status') {
      return true; // Pass through items that are not related to status
    }

    const recStatus = data?.status;
    const userRole = user?.role;
    const userModuleAccess = user?.moduleAccess;

    // Function to check if the user is an approver or admin
    const isApproverOrAdmin = () => {
      return (
        userRole === 'ROLE_ADMIN' ||
        isModuleApproved(userModuleAccess, 'ReceivingReport')
      );
    };

    switch (recStatus) {
      case RequestStatusEnum.CANCEL:
      case RequestStatusEnum.DONE:
        return false; // Filter out CANCEL and DONE statuses

      case RequestStatusEnum.DRAFT:
        // Allow if user is admin or the destination matches user's default location
        return userRole === 'ROLE_ADMIN' || userModuleAccessRole?.confirm;

      case RequestStatusEnum.CONFIRM:
        // Allow if user is admin or destination matches default location and user is an approver
        return (
          (isApproverOrAdmin() ||
            data?.destination?.id === userModuleAccessRole?.approve) &&
          user?.username !== data?.createdBy
        );

      case RequestStatusEnum.APPROVED:
      case RequestStatusEnum.AVAILABLE:
      case RequestStatusEnum.ISSUED:
        // Allow if destination matches default location and user is an approver or admin
        return (
          (isApproverOrAdmin() ||
            data?.destination?.id === userModuleAccessRole?.issuannce) &&
          user?.username !== data?.createdBy
        );

      default:
        return false; // Filter out any other statuses
    }
  });

  return (
    <>
      <Card style={{ margin: '1rem' }} title={'Status Timeline'}>
        <Timeline reverse={false} items={filteredTimelineItems} />
      </Card>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Receiving Report successfully set to ${data?.status}`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default ReceivingReportInfoStatus;
